'use client';

import React from 'react';
import Link from 'next/link';
import { Home, TrendingUp } from 'lucide-react';
import { useSite } from '@/context/SiteContext';
import { cn } from '@/lib/utils';

/**
 * "Return to home page" affordance for the auth pages.
 *
 * This used to be a floating pill rendered by LayoutShell above the card,
 * which cost a row of vertical space on every auth screen and pushed the
 * (already tall) create-account form below the fold. It now lives in the
 * card's top-left corner instead, so the card owns its own chrome.
 *
 * Icon-only by design: the auth cards are max-w-md/lg, and a labelled pill
 * plus the centred logo doesn't fit at 375px without the two colliding.
 */
export function AuthHomeButton({ className }: { className?: string }) {
  return (
    <Link
      href="/"
      aria-label="Return to home page"
      title="Return to home page"
      className={cn(
        'shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg',
        'border border-default bg-surface text-fg-muted',
        'hover:text-sky-300 hover:border-sky-500/50 hover:bg-card-hover-bg',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40 transition-colors',
        className
      )}
    >
      <Home size={15} />
    </Link>
  );
}

/**
 * Shared header for the auth cards: home button on the left, brand logo
 * optically centred (the right-hand spacer balances the button), then the
 * page title. Keeps /login, /create-account, /forgot-password and
 * /reset-password visually identical above the fold.
 */
export default function AuthCardHeader({
  title,
  subtitle,
  className,
}: {
  title: string;
  subtitle?: string;
  className?: string;
}) {
  const { siteName, logoUrl } = useSite();

  return (
    <div className={cn('mb-4', className)}>
      <div className="flex items-center gap-2">
        <AuthHomeButton />
        <Link href="/" className="flex-1 flex items-center justify-center gap-2 min-w-0">
          {logoUrl ? (
            <img src={logoUrl} alt={siteName} className="h-8 w-auto max-w-full object-contain" />
          ) : (
            <>
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-400 to-sky-500 flex items-center justify-center shrink-0">
                <TrendingUp size={18} className="text-white" />
              </div>
              <span className="font-bold text-lg bg-gradient-to-r from-emerald-400 to-sky-400 bg-clip-text text-transparent truncate">
                {siteName}
              </span>
            </>
          )}
        </Link>
        {/* Balances the home button so the logo sits optically centred. */}
        <span className="w-8 shrink-0" aria-hidden="true" />
      </div>

      <h1 className="mt-3 text-center text-xl font-bold text-fg">{title}</h1>
      {subtitle && <p className="mt-0.5 text-center text-xs text-fg-subtle">{subtitle}</p>}
    </div>
  );
}
